import type { ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { Navigate, Outlet, useLocation } from 'react-router';
import type { Permission } from '@portal/contracts';
import { AccessDenied } from '@/pages/status-pages';
import { ErrorState } from '@/components/common/page';
import { BusinessProvider, useBusiness } from './business';
import { useSession } from './session';

export function FullPageSpinner() {
  return (
    <div className="flex min-h-svh items-center justify-center" role="status" aria-label="Loading">
      <Loader2 className="size-8 animate-spin text-primary" />
    </div>
  );
}

/** Signed-in (and past the MFA step) or bounce to /login. Real enforcement is on the API; this is UX. */
export function RequireAuth() {
  const { session, isLoading, error, refetch } = useSession();
  const loc = useLocation();
  if (isLoading) return <FullPageSpinner />;
  if (error) return <div className="mx-auto max-w-lg p-8"><ErrorState error={error} onRetry={() => void refetch()} title="We couldn’t reach the server" /></div>;
  if (!session) return <Navigate to={`/login?next=${encodeURIComponent(loc.pathname + loc.search)}`} replace />;
  if (!session.mfaVerified) return <Navigate to={`/mfa?next=${encodeURIComponent(loc.pathname + loc.search)}`} replace />;
  return <Outlet />;
}

export function RedirectIfAuthed() {
  const { session, isLoading } = useSession();
  if (isLoading) return <FullPageSpinner />;
  if (session?.mfaVerified) return <Navigate to="/" replace />;
  return <Outlet />;
}

/** Users with no business yet are sent to onboarding. */
export function RequireBusiness() {
  const { session } = useSession();
  if (!session || session.memberships.length === 0) return <Navigate to="/onboarding" replace />;
  return (
    <BusinessProvider>
      <BusinessGate />
    </BusinessProvider>
  );
}

function BusinessGate() {
  const { context, isLoading, error } = useBusiness();
  if (isLoading || (!context && !error)) return <FullPageSpinner />;
  if (error) return <div className="mx-auto max-w-lg p-8"><ErrorState error={error} title="We couldn’t load your business" /></div>;
  return <Outlet />;
}

export function RequirePermission({ permission, children, what }: { permission: Permission; children: ReactNode; what?: string }) {
  const { can } = useBusiness();
  return can(permission) ? <>{children}</> : <AccessDenied what={what} />;
}
